import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { APPLICATION_CARD_ORDER } from "constants/pageNames";
import type {
    ContactInformationRef,
    CoverageReplacementRef,
    PolicyBeneficiaryRef,
    SecondaryAddresseeRef,
    StatementOfHealthRef,
} from "types/Card";

const [
    contactInfoCardId,
    statementOfHealthCardId,
    coverageReplacementCardId,
    policyBeneficiaryCardId,
    secondaryAddresseeCardId,
] = APPLICATION_CARD_ORDER;

interface UseContactCardRefsArgs {
    isDTO: boolean;
    isGIWL: boolean;
    isLoadingData: boolean;
}

/**
 * Creates the card refs, the id-keyed ref map used for validation/blur, the
 * smooth-scroll helper, and the URL-hash deep-link effect.
 *
 * The ref map only includes the statement-of-health and coverage-replacement
 * cards for the products that render them, so callers can iterate it safely.
 */
export const useContactCardRefs = ({ isDTO, isGIWL, isLoadingData }: UseContactCardRefsArgs) => {
    const location = useLocation();

    const contactInformationRef = useRef<ContactInformationRef>(null);
    const statementOfHealthRef = useRef<StatementOfHealthRef>(null);
    const coverageReplacementRef = useRef<CoverageReplacementRef>(null);
    const policyBeneficiaryRef = useRef<PolicyBeneficiaryRef>(null);
    const secondaryAddresseeRef = useRef<SecondaryAddresseeRef>(null);

    const cardRefs = useMemo(() => {
        const refs: Record<string, React.RefObject<any>> = {
            [contactInfoCardId]: contactInformationRef,
            [policyBeneficiaryCardId]: policyBeneficiaryRef,
            [secondaryAddresseeCardId]: secondaryAddresseeRef,
        };
        if (isDTO) {
            refs[statementOfHealthCardId] = statementOfHealthRef;
            refs[coverageReplacementCardId] = coverageReplacementRef;
        }
        if (isGIWL) {
            refs[coverageReplacementCardId] = coverageReplacementRef;
        }
        return refs;
    }, [isDTO, isGIWL]);

    const scrollToCard = useCallback((cardId: string) => {
        const element = document.getElementById(cardId);
        if (element) {
            const rect = element.getBoundingClientRect();
            const scrollTop = window.scrollY;
            const elementTop = rect.top + scrollTop - 120;

            window.scrollTo({
                top: Math.max(0, elementTop),
                behavior: "smooth",
            });
        }
    }, []);

    // Handle URL fragment navigation
    useEffect(() => {
        if (isLoadingData) return;

        const hash = location.hash.replace("#", "");
        if (hash && cardRefs[hash as keyof typeof cardRefs]) {
            requestAnimationFrame(() => scrollToCard(hash));
        }
    }, [location.hash, cardRefs, scrollToCard, isLoadingData]);

    return {
        contactInformationRef,
        statementOfHealthRef,
        coverageReplacementRef,
        policyBeneficiaryRef,
        secondaryAddresseeRef,
        cardRefs,
        scrollToCard,
    };
};
